<template>
    <!-- Clone Node Modal -->
    <base-modal id="conditional-branch-modal" :loading="modalLoading" :centered="true" size="large">
        <template slot="title">
            <span class="icon icon-glyphicons-basic-directions-arrow"></span>
            Conditional Branching
        </template>
        <template>
            <div class="mb-4">
                <p class="mb-2">
                    Control which screen the continue button will lead to, based on conditions. Conditions are checked
                    in the order they're listed here.
                </p>
            </div>
            <!-- Conditional Branching -->
            <div class="row mb-4">
                <div class="col-12" v-if="isLoaded">
                    <div v-show="conditionalBranchesEditable.length === 0" class="mb-2">
                        <br />
                        <p class="text-secondary text-center">No conditional branches added to this screen.</p>
                        <div class="d-flex justify-content-center">
                            <a class="badge badge-gray badge-small ml-1 mr-1" @click="addConditionalBranch()"
                                >+ Conditional Branch</a
                            >
                        </div>
                        <br />
                    </div>
                    <draggable v-model="conditionalBranchesEditable" v-bind="dragOptions">
                        <div
                            v-for="(conditionalBranch, index) in conditionalBranchesEditable"
                            :key="conditionalBranch.id"
                            tabindex="0"
                            :id="'condition-' + index"
                            @keyup.38="shiftConditionalBranchIndex(index, -1, $event)"
                            @keyup.40="shiftConditionalBranchIndex(index, 1, $event)"
                        >
                            <div class="card card-body bg-light py-2 pl-1 pr-2 mb-2">
                                <div class="d-flex">
                                    <div class="flex-grow-0 flex-shrink-0 mx-2 drag-handle">
                                        <span
                                            class="icon icon-lg icon-glyphicons-basic-set"
                                            style="margin-top: 12px"
                                        ></span>
                                    </div>
                                    <div class="flex-grow-0 flex-shrink-0" style="max-width: 470px">
                                        <!-- List of Conditions -->
                                        <div
                                            v-for="(condition, conditionIndex) in conditionalBranch.conditions"
                                            :key="condition.id"
                                            class="pb-1"
                                        >
                                            <div class="d-flex align-items-center">
                                                <!-- Condition Type -->
                                                <div>
                                                    <select v-model="condition.condition_type" class="form-control">
                                                        <option
                                                            v-for="option in conditionalBranchOptions"
                                                            :key="option.value"
                                                            :value="option.value"
                                                            :disabled="option.disabled"
                                                        >
                                                            {{ option.label }}
                                                        </option>
                                                    </select>
                                                </div>
                                                <!-- Count Input -->
                                                <div
                                                    class="flex-grow-0 flex-shrink-0 pl-1"
                                                    v-show="showCountInput(condition)"
                                                >
                                                    <input
                                                        v-model.number="condition.response_count"
                                                        type="number"
                                                        class="form-control"
                                                        style="width: 60px"
                                                    />
                                                </div>
                                                <!-- Choice Selector -->
                                                <div
                                                    v-if="condition.condition_type == 'prev_choice_was_selected'"
                                                    class="flex-shrink-0 pl-1"
                                                    style="min-width: 180px"
                                                >
                                                    <v-select
                                                        v-model="condition.choice_id"
                                                        :options="allSelectableChoices"
                                                        :close-on-select="true"
                                                        label="text"
                                                        class="mw-100"
                                                        placeholder="Select a choice..."
                                                        :reduce="(option) => option.id"
                                                    >
                                                        <template slot="option" slot-scope="option">
                                                            <span class="mr-1 small align-middle">
                                                                <span
                                                                    class="icon icon-100 mr-1"
                                                                    :class="getPanelIconClass(option.panel)"
                                                                ></span
                                                                ><span>{{ option.text }}</span></span
                                                            >
                                                            <span
                                                                class="badge badge-light badge-tiny badge-title align-middle"
                                                                >Screen {{ option.panel.node.number }}.
                                                                {{ option.panel.node.title }}</span
                                                            >
                                                        </template>
                                                        <template v-slot:selected-option="option">
                                                            <div
                                                                class="d-flex align-items-center nowrap"
                                                                style="max-width: 180px"
                                                            >
                                                                <span class="mr-1 small">
                                                                    <span
                                                                        class="icon icon-100 mr-1"
                                                                        :class="getPanelIconClass(option.panel)"
                                                                    ></span
                                                                    ><span>{{ option.text }}</span></span
                                                                >
                                                                <span
                                                                    class="badge badge-light badge-tiny badge-title-sm"
                                                                    >Screen {{ option.panel.node.number }}.
                                                                    {{ option.panel.node.title }}</span
                                                                >
                                                            </div>
                                                        </template>
                                                        <template slot="no-options">
                                                            <div class="py-2">
                                                                <span class="text-secondary"
                                                                    >No choices have been created yet.</span
                                                                >
                                                            </div>
                                                        </template>
                                                    </v-select>
                                                </div>
                                                <!-- Choice Selector (this screen) -->
                                                <div
                                                    class="flex-grow-0 flex-srink-0 pl-1"
                                                    v-show="
                                                        ['choice_is_selected', 'choice_is_not_selected'].includes(
                                                            condition.condition_type
                                                        )
                                                    "
                                                >
                                                    <select class="form-control" v-model="condition.choice_id">
                                                        <option
                                                            v-for="choice in selectableChoices"
                                                            :key="choice.id"
                                                            :value="choice.id"
                                                        >
                                                            {{ choice.text }}
                                                        </option>
                                                    </select>
                                                </div>
                                                <!-- Category Choice Selector -->
                                                <div
                                                    class="flex-grow-0 flex-srink-0 pl-1"
                                                    v-show="
                                                        [
                                                            'choice_categorized_correctly',
                                                            'choice_categorized_incorrectly',
                                                        ].includes(condition.condition_type)
                                                    "
                                                >
                                                    <select class="form-control" v-model="condition.choice_id">
                                                        <option
                                                            v-for="choice in categoriesChoices"
                                                            :key="choice.id"
                                                            :value="choice.id"
                                                        >
                                                            {{ choice.text }}
                                                        </option>
                                                    </select>
                                                </div>
                                                <!-- Choice Value Input -->
                                                <div
                                                    class="flex-grow-0 flex-srink-0 pl-1"
                                                    v-show="condition.condition_type == 'choice_value_is'"
                                                >
                                                    <div class="d-flex">
                                                        <select class="form-control mr-1" v-model="condition.choice_id">
                                                            <option
                                                                v-for="choice in choicesWithInput"
                                                                :key="choice.id"
                                                                :value="choice.id"
                                                            >
                                                                {{ choice.text }}
                                                            </option>
                                                        </select>
                                                        <input
                                                            v-model="condition.response_value"
                                                            type="text"
                                                            placeholder="Value"
                                                            class="form-control"
                                                            style="width: 100px"
                                                        />
                                                    </div>
                                                </div>
                                                <!-- Node Selector -->
                                                <div
                                                    class="flex-srink-0 pl-1"
                                                    v-show="condition.condition_type == 'node_was_visited'"
                                                    style="min-width: 230px"
                                                >
                                                    <base-search
                                                        placeholder="Select a screen..."
                                                        v-model="condition.condition_node_id"
                                                        :options="nodeOptions"
                                                        value-field="id"
                                                        label-field="label"
                                                        :search-fields="['label']"
                                                    ></base-search>
                                                </div>
                                                <div v-show="conditionalBranch.conditions.length > 1" class="pl-1">
                                                    <span
                                                        class="icon icon-sm icon-glyphicons-basic-times icon-btn text-secondary"
                                                        @click="
                                                            removeConditionFromBranch(conditionalBranch, conditionIndex)
                                                        "
                                                    ></span>
                                                </div>
                                            </div>
                                            <div v-if="condition.condition_type === 'ai_analysis'">
                                                <textarea
                                                    v-model="condition.ai_description"
                                                    type="text"
                                                    class="form-control form-control-sm mw-100 mt-2"
                                                    maxlength="1200"
                                                    placeholder="Describe the condition here..."
                                                    rows="3"
                                                    dir="auto"
                                                ></textarea>
                                            </div>
                                        </div>
                                        <button
                                            class="btn btn-light badge badge-small"
                                            @click="addConditionToBranch(conditionalBranch)"
                                        >
                                            + Condition
                                        </button>
                                    </div>
                                    <div class="flex-grow-0 flex-shrink-0 px-3">
                                        <span
                                            class="icon icon-xl icon-glyphicons-basic-arrow-right branch-arrow"
                                            style="margin-top: 8px"
                                        ></span>
                                    </div>
                                    <!-- Target Node -->
                                    <div class="flex-grow-1">
                                        <base-search
                                            placeholder="Target screen"
                                            v-model="conditionalBranch.target_node"
                                            :options="nodeOptions"
                                            value-field="id"
                                            label-field="label"
                                            :search-fields="['label']"
                                        ></base-search>
                                    </div>
                                    <div class="flex-grow-0 flex-shrink-0 pl-2" style="margin-top: 6px">
                                        <span
                                            class="icon icon-glyphicons-basic-bin icon-btn"
                                            @click="removeConditionalBranch(index)"
                                        ></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </draggable>
                    <div v-show="conditionalBranchesEditable.length > 0" class="pt-2">
                        <a class="badge badge-gray ml-1 mr-1" @click="addConditionalBranch()">+ Conditional Branch</a>
                    </div>
                </div>
            </div>
        </template>
        <template slot="footer">
            <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
            <button @click="save()" :disabled="modalLoading" class="btn btn-primary" type="button">Save</button>
        </template>
    </base-modal>
</template>

<script>
import http from "@/axios.js"
import draggable from "vuedraggable"
import BaseModal from "@/components/BaseModal"
import BaseSearch from "@/components/BaseSearch"
import { panelTypeIcons } from "@/assets/js/settings.js"

export default {
    name: "ConditionalBranchEditor",
    components: {
        BaseModal,
        BaseSearch,
        draggable,
    },
    props: {
        node: {
            type: Object,
            required: true,
        },
        panels: {
            type: Array,
            required: false,
            default() {
                return []
            },
        },
        conditionalBranches: {
            type: Array,
            required: true,
        },
        nodeOptions: {
            type: Array,
            required: true,
        },
        selectableChoices: {
            type: Array,
            required: true,
        },
        categoriesChoices: {
            type: Array,
            default() {
                return []
            },
        },
        choicesWithInput: {
            type: Array,
            required: true,
        },
        caseId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            conditionalBranchesEditable: this.conditionalBranches,
            dragOptions: {
                animation: 300,
                ghostClass: "ghost",
                handle: ".drag-handle",
            },
            modalLoading: false,
            allSelectableChoices: [],
            isLoaded: false,
        }
    },
    computed: {
        conditionalBranchOptions() {
            return [
                {
                    label: "No correct responses",
                    value: "no_correct_responses",
                },
                {
                    label: "All correct responses",
                    value: "all_correct_responses",
                },
                {
                    label: "Minimum correct responses",
                    value: "num_correct_responses",
                },
                {
                    label: "Minimum incorrect responses",
                    value: "num_incorrect_responses",
                },
                {
                    label: "Choice is selected",
                    value: "choice_is_selected",
                    disabled: this.selectableChoices.length == 0,
                },
                {
                    label: "Choice left unselected",
                    value: "choice_is_not_selected",
                    disabled: this.selectableChoices.length == 0,
                },
                {
                    label: "Choice was selected on another screen",
                    value: "prev_choice_was_selected",
                    disabled: this.allSelectableChoices.length == 0,
                },
                {
                    label: "Screen was visited",
                    value: "node_was_visited",
                },
                {
                    label: "Form input value equals",
                    value: "choice_value_is",
                    disabled: this.choicesWithInput.length == 0,
                },
                {
                    label: "Item was categorized correctly",
                    value: "choice_categorized_correctly",
                    disabled: this.categoriesChoices.length == 0,
                },
                {
                    label: "Item was categorized incorrectly",
                    value: "choice_categorized_incorrectly",
                    disabled: this.categoriesChoices.length == 0,
                },
                {
                    label: "AI response analysis condition (voice/text)",
                    value: "ai_analysis",
                    disabled: this.textAndVoiceResponsePanels.length === 0,
                },
                {
                    label: "All other cases (fallback)",
                    value: "else",
                },
            ]
        },
        textAndVoiceResponsePanels() {
            return this.panels.filter((p) => ["textarea", "voice_recording"].includes(p.panel_type))
        },
    },
    methods: {
        // Add a new conditional branch.
        addConditionalBranch() {
            this.conditionalBranchesEditable.push({
                target_node: null,
                // Initialize with one condition.
                conditions: [
                    {
                        condition_node: null,
                        choice_id: null,
                        response_value: "",
                        response_count: null,
                        condition_type: "num_correct_responses",
                    },
                ],
            })
        },
        // Remove a conditional branch.
        removeConditionalBranch(index) {
            this.conditionalBranchesEditable.splice(index, 1)
        },
        // Add a new condition to an existing branch.
        addConditionToBranch(conditionalBranch) {
            conditionalBranch.conditions.push({
                condition_node: null,
                choice_id: null,
                response_value: "",
                response_count: null,
                condition_type: "num_correct_responses",
            })
        },
        // Remove a condition from an existing branch.
        removeConditionFromBranch(conditionalBranch, index) {
            conditionalBranch.conditions.splice(index, 1)
        },
        // Open the conditional branching editor modal (called from parent).
        open() {
            this.modalLoading = true
            this.conditionalBranchesEditable = Object.assign([], this.conditionalBranches)
            $("#conditional-branch-modal").modal("show")
            // Fetch all possible choices in the case.
            let vm = this
            http.get(`/api/cases/${this.caseId}/choices/`, {
                params: {
                    panelType: "multichoice,multiselect,singleselect,image_hotspots",
                },
            })
                .then(function (response) {
                    vm.allSelectableChoices = response.data
                    vm.isLoaded = true
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Move a conditional branch up or down programatically (the primary way of re-ordering
        // conditions is via drag & drop).
        shiftConditionalBranchIndex(index, increment) {
            // Don't do anything if condition is already at the start or end of the array.
            if (
                (index == 0 && increment == -1) ||
                (index + 1 >= this.conditionalBranchesEditable.length && increment == 1)
            ) {
                return
            }
            // Remove the choice from the choice array.
            let condition = this.conditionalBranchesEditable.splice(index, 1)[0]
            // Put it back at the new position.
            let newIndex = index + increment
            this.conditionalBranchesEditable.splice(newIndex, 0, condition)
            this.$nextTick(() => document.getElementById("condition-" + newIndex).focus())
        },
        // Save conditional branches for the node.
        save() {
            this.modalLoading = true
            this.conditionalBranchesEditable.forEach((c, index) => (c.order = index))
            let vm = this
            http.put(`/api/nodes/${this.node.id}/conditionals/`, this.conditionalBranchesEditable)
                .then(function () {
                    $("#conditional-branch-modal").modal("hide")
                    vm.$emit("updated")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Conditional branches updated.",
                        data: {},
                    })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Returns true if the numeric count input should be shown for a given condition.
        showCountInput(condition) {
            return ["num_correct_responses", "num_incorrect_responses"].includes(condition.condition_type)
        },
        // Returns the icon associated with a given panel's type.
        getPanelIconClass(panel) {
            return panelTypeIcons[panel.panel_type]
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/nodemap.scss";

.drag-handle {
    cursor: move;
    cursor: grab;
}

.drag-handle:active {
    cursor: grabbing;
}

.badge-title {
    max-width: 240px;
    text-overflow: ellipsis;
    overflow: hidden;
}

.badge-title-sm {
    max-width: 150px;
    text-overflow: ellipsis;
    overflow: hidden;
}

.branch-arrow {
    color: $forward;
}
</style>